import { defineStore } from 'pinia';
import { store } from '@/store';
import { agentApi, type ProjectConfigForm, type CommonConfig, type PortalConfig } from '@/api';
import { Code, Lang, type SystemConfigState } from '@/config';
import { storageLocal } from '@pureadmin/utils';
import { themeColors } from '@/config';
// 本地存储 Key，将所有配置集中在一个项下
export const CONFIG_KEY = 'system-config';

export const useSystemConfigStore = defineStore('systemConfig', {
  state: (): SystemConfigState => {
    // 尝试从 localStorage 读取完整配置
    const saved = storageLocal().getItem<SystemConfigState>(CONFIG_KEY);
    if (saved) return saved;
    // 默认值
    const defaultLang = Lang.LAN_CN;
    const fallbackColor = themeColors[0]?.color;
    return {
      commonConfig: {} as CommonConfig,
      portalConfig: {} as PortalConfig,
      currentLang: defaultLang,
      primaryColor: fallbackColor,
    };
  },

  actions: {
    /** 获取并同步后端配置 */
    async getSystemConfig() {
      const res = await agentApi.reqGetSystemConfig();
      if (res.statusCode === Code.SUCCESS.statusCode) {
        const { data } = res;
        dealGlobalService(this, data);
        // 持久化整个 state
        storageLocal().setItem(CONFIG_KEY, {
          commonConfig: this.commonConfig,
          portalConfig: this.portalConfig,
          currentLang: this.currentLang,
          primaryColor: this.primaryColor,
        });
      }
    },
    /** 设置语言并持久化 */
    setLang(lang: Lang) {
      this.currentLang = lang;
      storageLocal().setItem(CONFIG_KEY, {
        commonConfig: this.commonConfig,
        portalConfig: this.portalConfig,
        currentLang: this.currentLang,
        primaryColor: this.primaryColor,
      });
    },
  },
});

export function useSystemConfigStoreHook() {
  return useSystemConfigStore(store);
}

/**
 * 将后端下发的 global 配置同步到 store，并应用主题色
 */
function dealGlobalService(
  store: ReturnType<typeof useSystemConfigStore>,
  data: ProjectConfigForm
) {
  // 通用 & 门户配置
  if (data.commonConfig) store.commonConfig = data.commonConfig;
  if (data.portalConfig) store.portalConfig = data.portalConfig;

  // 语言处理：首选 store.currentLang，否则取后端首选
  const supported = data.commonConfig?.lang ?? [];
  let chosen = store.currentLang;
  if (supported.length) {
    chosen = supported.includes(chosen) ? chosen : (supported[0] as Lang);
  }
  store.currentLang = chosen;

  // 主题色由 commonConfig.theme 指定
  const themeKey = data.commonConfig?.theme;
  const themeObj = themeColors.find((tc) => tc.value === themeKey);

  if (themeObj) {
    store.primaryColor = themeObj.color;
  }

  // 更新 CSS 变量
  document.documentElement.style.setProperty('--color-main-primary', store.primaryColor);
}
